// ═══════════════════════════════════════════════════════════════
// MACROS DE CASO — Secuencias de acciones aplicables con un click
// ═══════════════════════════════════════════════════════════════
//
// Una macro es una lista ordenada de operaciones que se ejecutan
// secuencialmente sobre un caso (con la lib casos.js). Útil para
// patrones repetitivos como "Resolver con reembolso 100%" que
// implica: comentar plantilla + cambiar estado + crear tarea + cerrar.
//
// Schema:
//   {
//     codigo, nombre, descripcion,
//     tiposCasoAplicables: ['reclamo-ml','devolucion-ml'],
//     acciones: [
//       { tipo: 'agregarComentario', visibilidad, contenido },
//       { tipo: 'cambiarEstado', estadoNuevo, motivo },
//       { tipo: 'agregarParticipante', participante },
//       { tipo: 'crearTarea', tareaData },
//       { tipo: 'cerrarCaso', conclusion, motivoCierre, estadoFinal },
//     ],
//     activo, orden
//   }
// ═══════════════════════════════════════════════════════════════

import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
import { forPlatformCreate, forUpdate } from './dbHelpers'
// `casos` se importa lazy (cycle), pero `tiposCaso` es hoja: nadie lo importa
// desde acá arriba, así que el enum entra estático.
import { CASO_ESTADOS } from './tiposCaso'

// ──────── ABM ────────

export async function listarMacrosCaso({ soloActivas = true, tipoCasoCodigo = null } = {}) {
  const snap = await getDocs(collection(db, COLLECTIONS.MACROS_CASO))
  let list = snap.docs.map(docConId)
  if (soloActivas) list = list.filter(m => m.activo !== false)
  if (tipoCasoCodigo) {
    list = list.filter(m =>
      !m.tiposCasoAplicables?.length ||
      m.tiposCasoAplicables.includes(tipoCasoCodigo) ||
      m.tiposCasoAplicables.includes('*')
    )
  }
  list.sort((a, b) => (a.orden || 0) - (b.orden || 0))
  return list
}

export async function getMacroCaso(macroId) {
  const snap = await getDoc(doc(db, COLLECTIONS.MACROS_CASO, macroId))
  return snap.exists() ? docConId(snap) : null
}

export async function upsertMacroCaso(macroId, datos) {
  const data = {
    codigo: datos.codigo || null,
    nombre: datos.nombre || 'Macro',
    descripcion: datos.descripcion || '',
    tiposCasoAplicables: Array.isArray(datos.tiposCasoAplicables) ? datos.tiposCasoAplicables : ['*'],
    acciones: Array.isArray(datos.acciones) ? datos.acciones : [],
    activo: datos.activo !== false,
    orden: Number(datos.orden) || 100,
  }
  if (!macroId) {
    const payload = forPlatformCreate(data)
    const ref = await addDoc(collection(db, COLLECTIONS.MACROS_CASO), payload)
    return { id: ref.id, ...payload }
  }
  const payload = forUpdate(data)
  await updateDoc(doc(db, COLLECTIONS.MACROS_CASO, macroId), payload)
  return { id: macroId, ...payload }
}

// ──────── Ejecución ────────

/** ¿La macro crea una tarea en alguno de sus pasos? */
export const macroCreaTarea = (macro) => Array.isArray(macro.acciones) && macro.acciones.some(a => a?.tipo === 'crearTarea')

/**
 * Ejecuta una macro sobre un caso. Las acciones se ejecutan secuencialmente.
 * Si una falla, se interrumpe la secuencia y se loguea (no se rollback —
 * las acciones anteriores ya quedaron escritas).
 *
 * @param {string} macroId
 * @param {string} casoId
 * @param {string} autor — userId del que ejecuta
 * @param {{ puedeCrearTarea?: boolean }} [opts] — si quien ejecuta puede crear
 *   tareas (`isConsultor()`, lo que piden las reglas). Sin `true`, una macro que
 *   crea una tarea no arranca: fallar cerrado.
 * @returns {Promise<{ ejecutadas, total, error? }>}
 */
export async function ejecutarMacro(macroId, casoId, autor = null, { puedeCrearTarea = false } = {}) {
  const macro = await getMacroCaso(macroId)
  if (!macro) throw new Error(`Macro ${macroId} no encontrada`)
  if (!macro.activo) throw new Error(`Macro ${macro.codigo} está desactivada`)

  // Importamos lazy para evitar cycle
  const casos = await import('./casos')
  const tareas = await import('./tareas')

  // ADR 2026-10-08: una macro que crea una tarea pregunta por Tareas ANTES del
  // primer paso. La puerta de `crearAccionable` está a mitad del loop: sin
  // esto, los pasos previos quedarían escritos sin rollback. El caso se lee
  // una vez acá y lo usa el paso `crearTarea` (ningún paso cambia su empresa).
  // Y por quien ejecuta: las reglas reservan el alta de tareas al staff, así
  // que a un cliente le rebotaba en ese paso con los anteriores ya escritos
  // (roadmap C5nW5b0MoxBoq9Vw4Vob, ítem 10).
  let caso = null
  if (macroCreaTarea(macro)) {
    if (puedeCrearTarea !== true) {
      const err = new Error('Esta macro crea una tarea, y tu cuenta no puede crear tareas.')
      err.causa = 'sin-permiso-tareas'
      throw err
    }
    caso = await casos.getCaso(casoId)
    if (!caso) throw new Error('Caso no encontrado')
    await tareas.exigirTareasOperables(caso.empresaId)
  }

  let ejecutadas = 0
  const total = macro.acciones?.length || 0

  for (const accion of (macro.acciones || [])) {
    try {
      switch (accion.tipo) {
        case 'agregarComentario':
          await casos.agregarComentario(
            casoId,
            accion.contenido || `[Macro: ${macro.nombre}]`,
            accion.visibilidad || casos.FEED_VISIBILIDAD.INTERNO,
            autor,
          )
          break
        case 'cambiarEstado': {
          const motivo = accion.motivo || `Macro: ${macro.nombre}`
          // Los cuatro estados con wrapper no se escriben por la transición
          // cruda; el porqué, en `casos.js`. Se ENRUTA en vez de dejar fallar
          // siempre que el dato alcance: una macro ya configurada con
          // `estadoNuevo: 'reabierto'` o `'cerrado'` sigue andando, y ahora
          // bien. Molde: la acción `cerrarCaso` de acá abajo, que por lo mismo
          // llama al wrapper.
          if (accion.estadoNuevo === CASO_ESTADOS.REABIERTO) {
            await casos.reabrirCaso(casoId, motivo, autor)
          } else if (
            accion.estadoNuevo === CASO_ESTADOS.CERRADO ||
            accion.estadoNuevo === CASO_ESTADOS.CERRADO_SIN_ACCION
          ) {
            // El default `'otro'` es el mismo que usa la acción `cerrarCaso`.
            await casos.cerrarCaso(
              casoId,
              accion.conclusion || motivo,
              accion.motivoCierre || 'otro',
              autor,
              { estadoFinal: accion.estadoNuevo },
            )
          } else if (accion.estadoNuevo === CASO_ESTADOS.ESCALADO) {
            // Acá no se puede enrutar: `escalarCaso` necesita un
            // `supervisorId` que esta acción no tiene, y fabricarlo escalaría
            // el caso a nadie. Fail-closed, nombrando el dato que falta y la
            // salida. No inventa un modo de falla nuevo: el catch de abajo ya
            // corta la secuencia y devuelve `{ error }`, que la UI muestra.
            // ⚠️ Las acciones previas de la macro ya se aplicaron y no hay
            // rollback: la macro queda a mitad, con la causa visible.
            // Deuda con trigger: si una macro necesita escalar, se agrega una
            // acción `escalarCaso` con `supervisorId`, espejando la acción
            // `cerrarCaso` que ya existe.
            throw new Error(
              'una macro no puede escalar por cambiarEstado: falta supervisorId. ' +
              'Hace falta una acción escalarCaso con ese dato.',
            )
          } else {
            await casos.cambiarEstado(casoId, accion.estadoNuevo, motivo, autor)
          }
          break
        }
        case 'agregarParticipante':
          await casos.agregarParticipante(casoId, accion.participante)
          break
        case 'crearTarea': {
          await tareas.crearAccionable(caso.empresaId, {
            ...accion.tareaData,
            casoId,
            asignadoPor: autor,
          })
          break
        }
        case 'cerrarCaso':
          await casos.cerrarCaso(
            casoId,
            accion.conclusion || `Macro: ${macro.nombre}`,
            accion.motivoCierre || 'otro',
            autor,
            { estadoFinal: accion.estadoFinal },
          )
          break
        default:
          console.warn(`[macro] acción desconocida: ${accion.tipo}`)
      }
      ejecutadas++
    } catch (e) {
      console.error(`[macro] acción ${accion.tipo} falló:`, e.message)
      return { ejecutadas, total, error: e.message }
    }
  }

  return { ejecutadas, total }
}

// ──────── Window debug ────────
if (typeof window !== 'undefined') {
  window.__macrosCaso = {
    listarMacrosCaso, getMacroCaso, upsertMacroCaso, ejecutarMacro,
  }
}
